import { Link } from "react-router-dom";
import { ArrowUpRight, Check } from "lucide-react";
import AnchorMark from "@/components/AnchorMark";

const focusAreas = [
  "Emotional resilience",
  "Nervous system regulation",
  "Decision-making",
  "Boundaries",
  "Self-trust",
  "Navigating major life transitions",
  "Staying grounded under pressure",
];

const outcomes = [
  "Less reactivity",
  "Less overthinking",
  "Faster recovery from challenges",
  "Greater clarity",
  "More confidence in your decisions",
  "A deeper sense of calm and stability",
];

export default function Coaching() {
  return (
    <section
      id="coaching"
      data-testid="coaching-section"
      className="relative py-24 md:py-40"
      style={{ backgroundColor: "#EBE8E0" }}
    >
      <div className="max-w-7xl mx-auto px-6 md:px-12">
        {/* Intro */}
        <div className="grid grid-cols-1 md:grid-cols-12 gap-12 mb-20 md:mb-28">
          <div className="md:col-span-5">
            <p className="overline mb-6">06 — The work</p>
            <h2
              className="serif text-5xl md:text-6xl text-[#15243D] leading-[1.05] tracking-tight"
              style={{ fontWeight: 300 }}
            >
              1:1 Anchor<br />
              <em className="italic text-[#C07865]">Coaching.</em>
            </h2>
            <p
              className="mt-6 text-xs tracking-[0.22em] uppercase text-[#C07865]"
              data-testid="coaching-icf-credential"
            >
              ICF Certified Coach
            </p>
          </div>
          <div className="md:col-span-6 md:col-start-7 md:pt-4">
            <p className="text-lg md:text-xl font-light leading-relaxed text-[#5A5D63]">
              For people carrying a lot of responsibility who want to move
              through life with greater clarity and less internal friction.
            </p>
          </div>
        </div>

        {/* Two-column lists */}
        <div className="grid grid-cols-1 md:grid-cols-12 gap-px bg-[#15243D]/10">
          {/* Focus areas */}
          <div
            className="md:col-span-7 bg-[#F7F5F0] p-10 md:p-16"
            data-testid="coaching-focus-areas"
          >
            <div className="flex items-baseline justify-between mb-10">
              <p className="overline">Together we work on</p>
              <span
                className="serif text-2xl text-[#8A8D93]"
                style={{ fontWeight: 300 }}
              >
                I
              </span>
            </div>
            <ul className="space-y-5">
              {focusAreas.map((item) => (
                <li
                  key={item}
                  className="flex items-baseline gap-4 text-lg font-light text-[#15243D] border-b border-[#15243D]/8 pb-4 last:border-b-0"
                >
                  <span className="text-[#C07865] text-xs tracking-[0.2em]">—</span>
                  <span className="leading-relaxed">{item}</span>
                </li>
              ))}
            </ul>
          </div>

          {/* What changes */}
          <div
            className="md:col-span-5 bg-[#F7F5F0] p-10 md:p-16 relative overflow-hidden"
            data-testid="coaching-outcomes"
          >
            <AnchorMark
              size={180}
              color="#15243D"
              className="absolute -right-12 -bottom-10 opacity-[0.06] pointer-events-none"
            />
            <div className="relative">
              <div className="flex items-baseline justify-between mb-10">
                <p className="overline">What changes</p>
                <span
                  className="serif text-2xl text-[#8A8D93]"
                  style={{ fontWeight: 300 }}
                >
                  II
                </span>
              </div>
              <ul className="space-y-5">
                {outcomes.map((item) => (
                  <li
                    key={item}
                    className="flex items-start gap-4 text-base font-light text-[#15243D]"
                  >
                    <Check
                      size={16}
                      strokeWidth={1.5}
                      className="text-[#C07865] mt-1.5 flex-shrink-0"
                    />
                    <span className="leading-relaxed">{item}</span>
                  </li>
                ))}
              </ul>
            </div>
          </div>
        </div>

        {/* CTA */}
        <div className="mt-16 md:mt-20 grid grid-cols-1 md:grid-cols-12 gap-8 items-baseline">
          <p
            className="md:col-span-7 serif text-2xl md:text-3xl text-[#15243D] leading-snug max-w-2xl"
            style={{ fontWeight: 300 }}
          >
            If something here feels like it’s <em className="italic text-[#C07865]">for you</em> — the next step is a quiet one.
          </p>
          <div className="md:col-span-4 md:col-start-9">
            <Link
              to="/apply"
              data-testid="coaching-apply-button"
              className="btn-primary"
            >
              Apply for coaching
              <ArrowUpRight size={16} strokeWidth={1.5} />
            </Link>
          </div>
        </div>
      </div>
    </section>
  );
}
